import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import {
  api,
  getStoredToken,
  PreferenceResponse,
  setStoredToken,
  SubscriptionResponse
} from "./api/client";
import { buildDeviceLabel, getClientContext } from "./lib/platform";
import { enablePushNotifications, ensureServiceWorker } from "./lib/push";

type Screen = "loading" | "welcome" | "verify" | "home";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

export function App() {
  const [screen, setScreen] = useState<Screen>("loading");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [userUuid, setUserUuid] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [preferences, setPreferences] = useState<PreferenceResponse[]>([]);
  const [subscriptions, setSubscriptions] = useState<SubscriptionResponse[]>([]);
  const [pushReady, setPushReady] = useState(false);
  const [contextTick, setContextTick] = useState(0);
  const [deferredInstall, setDeferredInstall] = useState<BeforeInstallPromptEvent | null>(null);

  const ctx = useMemo(() => getClientContext(), [contextTick]);
  const mustInstallFirst = ctx.mustInstallFirst;
  const deviceLabel = useMemo(() => buildDeviceLabel(ctx), [ctx]);
  const activeDevices = useMemo(() => subscriptions.filter((s) => s.isActive), [subscriptions]);

  const refreshPreferences = useCallback(async () => {
    const list = await api.listPreferences();
    setPreferences(list.filter((p) => p.channel === "web_push"));
  }, []);

  const refreshSubscriptions = useCallback(async () => {
    const list = await api.listSubscriptions();
    setSubscriptions(list);
    setPushReady(list.some((s) => s.isActive));
  }, []);

  useEffect(() => {
    const onChange = () => setContextTick((n) => n + 1);
    const media = window.matchMedia("(display-mode: standalone)");
    media.addEventListener?.("change", onChange);
    window.addEventListener("appinstalled", onChange);
    return () => {
      media.removeEventListener?.("change", onChange);
      window.removeEventListener("appinstalled", onChange);
    };
  }, []);

  useEffect(() => {
    const onBeforeInstall = (event: Event) => {
      event.preventDefault();
      setDeferredInstall(event as BeforeInstallPromptEvent);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, []);

  useEffect(() => {
    void (async () => {
      try {
        await ensureServiceWorker();
      } catch {
        /* ignore on unsupported browsers during first paint */
      }

      const token = getStoredToken();
      if (!token) {
        setScreen("welcome");
        return;
      }

      try {
        const me = await api.me();
        setUserUuid(me.userUuid);
        setUserEmail(me.email ?? null);
        await refreshPreferences();
        await refreshSubscriptions();
        setScreen("home");
      } catch {
        setStoredToken(null);
        setScreen("welcome");
      }
    })();
  }, [refreshPreferences, refreshSubscriptions]);

  async function onStartEmailSignIn() {
    if (mustInstallFirst) {
      setError("Install this app to the Home Screen first, then open it from the icon before signing in.");
      return;
    }
    if (!email.trim()) {
      setError("Email is required so this device attaches to your account.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.authStart(email.trim());
      setScreen("verify");
      setMessage("Check your email for a 6-digit code. Use the same email on every device.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not send code.");
    } finally {
      setBusy(false);
    }
  }

  async function onVerify(event: FormEvent) {
    event.preventDefault();
    if (mustInstallFirst) {
      setError("Open CNP from the Home Screen icon before signing in.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await api.authVerify(email.trim(), code, displayName || undefined);
      setStoredToken(result.accessToken);
      setUserUuid(result.userUuid);
      setUserEmail(result.email ?? email.trim().toLowerCase());
      await refreshPreferences();
      await refreshSubscriptions();
      setScreen("home");
      setMessage("Signed in. Enable notifications on this device to add it to your account.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Invalid code.");
    } finally {
      setBusy(false);
    }
  }

  async function onEnablePush() {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      if (mustInstallFirst) {
        throw new Error(
          ctx.platform === "ios"
            ? "On iPhone/iPad, add this app to the Home Screen and open it from the icon first."
            : "Install this app, then open it from the icon before enabling notifications."
        );
      }
      await enablePushNotifications();
      await refreshSubscriptions();
      setMessage("This device is now an active device on your account. Push goes to all active devices.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not enable notifications.");
    } finally {
      setBusy(false);
    }
  }

  async function onToggleDevice(sub: SubscriptionResponse, activate: boolean) {
    setBusy(true);
    setError(null);
    try {
      if (activate) {
        await api.activateDevice(sub.subscriptionId);
      } else {
        await api.deactivateDevice(sub.subscriptionId);
      }
      await refreshSubscriptions();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not update device.");
    } finally {
      setBusy(false);
    }
  }

  async function onAndroidInstall() {
    if (!deferredInstall) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await deferredInstall.prompt();
      await deferredInstall.userChoice;
      setDeferredInstall(null);
      setMessage("If install completed, open CNP from the home screen icon, then continue here.");
      setContextTick((n) => n + 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not start install.");
    } finally {
      setBusy(false);
    }
  }

  async function onTogglePreference(pref: PreferenceResponse, optedIn: boolean) {
    setError(null);
    try {
      await api.setPreference(pref.sourceKey, pref.typeKey, pref.channel, optedIn);
      setPreferences((current) =>
        current.map((item) =>
          item.sourceKey === pref.sourceKey &&
          item.typeKey === pref.typeKey &&
          item.channel === pref.channel
            ? { ...item, optedIn }
            : item
        )
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not update preference.");
    }
  }

  async function onTestSend() {
    setBusy(true);
    setError(null);
    try {
      const result = await api.testSend(
        "CNP test",
        "If you see this, web push delivery works.",
        "/"
      );
      setMessage(`Test queued (${result.enqueued} item(s)) for your user — all active devices.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Test send failed.");
    } finally {
      setBusy(false);
    }
  }

  function signOut() {
    setStoredToken(null);
    setUserUuid(null);
    setUserEmail(null);
    setPreferences([]);
    setSubscriptions([]);
    setPushReady(false);
    setScreen("welcome");
  }

  const installGate = mustInstallFirst ? (
    <div className="card stack install-gate">
      <h2>Install before you continue</h2>
      <p className="muted">
        Sign in and register devices only from the installed app, so this phone keeps one login for your email account.
      </p>
      <p className="muted">
        Detected device: <strong>{deviceLabel}</strong>
        {" · "}
        mode: <code>{ctx.displayMode}</code>
      </p>

      {ctx.platform === "ios" && (
        <ol className="steps">
          <li>Open this page in <strong>Safari</strong></li>
          <li>Tap <strong>Share</strong> → <em>Add to Home Screen</em></li>
          <li>Open <strong>CNP</strong> from the icon, then sign in with email</li>
        </ol>
      )}

      {ctx.platform === "android" && (
        <>
          <ol className="steps">
            <li>Install CNP to the home screen</li>
            <li>Open from the icon, then sign in with email and enable notifications</li>
          </ol>
          {deferredInstall && (
            <button type="button" disabled={busy} onClick={() => void onAndroidInstall()}>
              Install app
            </button>
          )}
        </>
      )}
    </div>
  ) : null;

  return (
    <div className="app">
      <header>
        <h1>CNP Preferences</h1>
        <p>One account per email · notifications to all your active devices.</p>
      </header>

      {message && <div className="banner info">{message}</div>}
      {error && <div className="banner error">{error}</div>}

      {installGate}

      {screen === "loading" && <div className="card muted">Loading…</div>}

      {screen === "welcome" && !mustInstallFirst && (
        <div className="card stack">
          <h2>Sign in with email</h2>
          <p className="muted">
            Use the same email on every phone or browser. After sign-in, enable notifications to add this device
            to your account. Messages are sent to your user id and delivered to every active device.
          </p>
          <p className="muted">
            This device: <strong>{deviceLabel}</strong>
            {ctx.isStandalone ? " · installed app" : ""}
          </p>
          <label>
            Display name (optional)
            <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Ada" />
          </label>
          <label>
            Email
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
            />
          </label>
          <div className="row">
            <button type="button" disabled={busy || !email.trim()} onClick={() => void onStartEmailSignIn()}>
              Email me a code
            </button>
          </div>
        </div>
      )}

      {screen === "verify" && !mustInstallFirst && (
        <form className="card stack" onSubmit={(e) => void onVerify(e)}>
          <h2>Enter sign-in code</h2>
          <p className="muted">Sent to {email}</p>
          <label>
            6-digit code
            <input
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="123456"
            />
          </label>
          <div className="row">
            <button type="submit" disabled={busy || code.trim().length < 4}>
              Verify and continue
            </button>
            <button type="button" className="secondary" onClick={() => setScreen("welcome")}>
              Back
            </button>
          </div>
        </form>
      )}

      {screen === "home" && !mustInstallFirst && (
        <>
          <div className="card stack">
            <h2>Your account</h2>
            <p className="muted">
              {userEmail ? (
                <>
                  Signed in as <strong>{userEmail}</strong>
                </>
              ) : (
                "Signed in"
              )}
              {" · "}
              <code>{userUuid}</code>
            </p>
            <p className="muted">
              This device: <strong>{deviceLabel}</strong>
              {" · "}
              <code>{ctx.displayMode}</code>
              {" · "}
              {activeDevices.length} active device{activeDevices.length === 1 ? "" : "s"}
            </p>
            <div className="row">
              <button type="button" className="success" disabled={busy} onClick={() => void onEnablePush()}>
                {pushReady ? "Refresh this device" : "Add this device (enable notifications)"}
              </button>
              <button type="button" className="secondary" disabled={busy || !pushReady} onClick={() => void onTestSend()}>
                Send test to all active devices
              </button>
              <button type="button" className="secondary" onClick={signOut}>
                Sign out
              </button>
            </div>
          </div>

          <div className="card stack">
            <h2>Devices</h2>
            <p className="muted">Active devices receive push. Deactivate a device to pause it without deleting.</p>
            {subscriptions.length === 0 && (
              <p className="muted">No devices yet. Enable notifications on this device.</p>
            )}
            {subscriptions.map((sub) => (
              <div className="device-item" key={sub.subscriptionId}>
                <div>
                  <strong>
                    {sub.deviceLabel || sub.platform || "Device"}
                    {!sub.isActive ? " (inactive)" : ""}
                  </strong>
                  <span className="muted">
                    {sub.platform}
                    {sub.lastSeenAt ? ` · last seen ${new Date(sub.lastSeenAt).toLocaleString()}` : ""}
                  </span>
                </div>
                <div className="row">
                  {sub.isActive ? (
                    <button
                      type="button"
                      className="secondary"
                      disabled={busy}
                      onClick={() => void onToggleDevice(sub, false)}
                    >
                      Deactivate
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="secondary"
                      disabled={busy}
                      onClick={() => void onToggleDevice(sub, true)}
                    >
                      Activate
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="card">
            <h2>Notification types</h2>
            {preferences.length === 0 && <p className="muted">No notification types yet.</p>}
            {preferences.map((pref) => (
              <div className="pref-item" key={`${pref.sourceKey}:${pref.typeKey}:${pref.channel}`}>
                <div>
                  <strong>{pref.typeDisplayName}</strong>
                  <span className="muted">
                    {pref.sourceName} · {pref.category}
                  </span>
                </div>
                <label className="switch" aria-label={`Toggle ${pref.typeDisplayName}`}>
                  <input
                    type="checkbox"
                    checked={pref.optedIn}
                    onChange={(e) => void onTogglePreference(pref, e.target.checked)}
                  />
                  <span className="slider" />
                </label>
              </div>
            ))}
          </div>
        </>
      )}

      {screen === "home" && mustInstallFirst && (
        <div className="banner warn">
          You are signed in inside the browser. Open the Home Screen app to manage devices on this account.
        </div>
      )}
    </div>
  );
}
